import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import jobs from "../data/jobs";
import { ArrowLeft, MapPin, DollarSign, Briefcase, Calendar, CheckCircle2 } from "lucide-react";

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const job = jobs.find((job) => job.id === parseInt(id));

  if (!job) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="bg-red-50 p-4 rounded-full mb-4">
          <Briefcase className="text-red-500 w-12 h-12" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">Job Not Found</h2>
        <p className="text-slate-500 mb-6">The job you're looking for might have been removed or doesn't exist.</p>
        <button onClick={() => navigate("/")} className="btn-primary">
          Back to Careers
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors mb-8 group"
      >
        <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
        Back to Search
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <div className="glass-card rounded-3xl p-5 sm:p-8 mb-8 border border-white/40">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-blue-600 p-4 rounded-2xl shadow-lg shadow-blue-500/20">
                <Briefcase className="text-white w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900">{job.title}</h1>
                <p className="text-blue-600 font-semibold">{job.company}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl text-slate-600 font-medium">
                <MapPin size={18} />
                Remote / Hybrid
              </div>
              <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl text-slate-600 font-medium">
                <DollarSign size={18} />
                {job.salary}
              </div>
              <div className="flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-xl text-slate-600 font-medium">
                <Calendar size={18} />
                Posted 2 days ago
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Job Description</h3>
              <p className="text-slate-600 leading-relaxed mb-8">{job.description}</p>

              <h3 className="text-xl font-bold text-slate-900 mb-4">Requirements</h3>
              <ul className="space-y-3 mb-8">
                {String(job.requirements || "Experience with modern web technologies, Strong communication skills, Team player").split(",").map((req, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle2 size={20} className="text-green-500 shrink-0 mt-0.5" />
                    <span>{req.trim()}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="glass-card rounded-3xl p-8 sticky top-32 border border-white/40">
            <h3 className="text-xl font-bold text-slate-900 mb-6">Apply for this role</h3>
            <p className="text-slate-500 mb-8 text-sm leading-relaxed">
              If your skills and experience match this role, we'd love to hear from you!
            </p>
            <button
              onClick={() => navigate(`/apply/${job.id}`)}
              className="w-full btn-primary !py-4 shadow-xl shadow-slate-900/10 mb-4"
            >
              Apply Now
            </button>
            <p className="text-center text-xs text-slate-400">
              Process takes less than 2 minutes
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;

